/**
 * For fetching and managing data about companies,
 * this is not for editing company details, see companies.ts, (those functionalities can be used across pages other than 1 company)
 */

import type { ICompanyConfigs } from '@/types';
import { getCache } from '@/utils/cache';
import { del, get, put, post } from '@/utils/request';
import { defineStore } from 'pinia';

const emptyCampaignsSearchForm = {
  campaign_statuses: [] as string[],
  name: '',
  page_number: 1,
  page_size: 10,
  sortings: [] as any[],
};

export const useCompanyStore = defineStore('company', {
  state: () => ({
    company: null as any,
    campaignsLoading: false,
    campaigns: null as any[] | null,
    campaignsFromSearch: null as any[] | null,
    totalCampaignsFromSearch: 0,
    campaignsSearchForm: emptyCampaignsSearchForm,
    contracts: null as any[] | null,
    influencers: [] as any[],
    isOldUser: getCache('old_user') || false,
    stats: null as any,
    group: null as any,
    groups: null as any[] | null,
    loading: false,
    error: null as string | any,
    success: null as string | any,
    isInitialising: true,

    // Cache implementation
    cache: {
      companyInit: { data: null, timestamp: null, companyId: null },
      companyDetails: { data: null, timestamp: null, companyId: null },
      campaigns: { data: null, timestamp: null, companyId: null },
    } as Record<'companyInit' | 'companyDetails' | 'campaigns', { data: any; timestamp: number | null; companyId: string | null }>,
    cacheConfig: {
      duration: 10 * 60 * 1000,
    },
  }),

  getters: {
    isCacheValid: state => (cacheKey: 'companyInit' | 'companyDetails' | 'campaigns', companyId?: string) => {
      const entry = state.cache[cacheKey];
      const isValid = entry?.data && entry?.timestamp && Date.now() - entry.timestamp < state.cacheConfig.duration;

      // For company-specific cache, also check if it's for the same company
      if (companyId && entry?.companyId) {
        return isValid && entry.companyId === companyId;
      }

      return isValid;
    },
  },

  actions: {
    async init(id: string, forceRefresh = false) {
      const cacheKey = 'companyInit';
      this.isInitialising = true;

      // Check if we have valid cached init data for this company
      if (!forceRefresh && this.isCacheValid(cacheKey, id)) {
        const cachedData = this.cache[cacheKey].data;

        // Restore state from cache
        this.company = cachedData.company;
        this.campaignsFromSearch = cachedData.campaignsFromSearch;
        this.totalCampaignsFromSearch = cachedData.totalCampaignsFromSearch;
        this.campaignsSearchForm = cachedData.campaignsSearchForm;

        this.isInitialising = false;

        return;
      }
      try {
        // Fetch fresh data
        await this.doGetCompanyDetails(id);
        this.campaignsSearchForm = { ...emptyCampaignsSearchForm };
        // await this.doSearchForCampaigns(id);

        // Cache the complete init state
        this.cache[cacheKey] = {
          data: {
            company: this.company,
            campaignsFromSearch: this.campaignsFromSearch,
            totalCampaignsFromSearch: this.totalCampaignsFromSearch,
            campaignsSearchForm: { ...this.campaignsSearchForm },
          },
          timestamp: Date.now(),
          companyId: id,
        };
      } catch (e) {
        this.error = e;

        // Optionally return cached data on error if available
        if (this.cache[cacheKey]?.data) {
          const cachedData = this.cache[cacheKey].data;

          this.company = cachedData.company;
          this.campaignsFromSearch = cachedData.campaignsFromSearch;
          this.totalCampaignsFromSearch = cachedData.totalCampaignsFromSearch;
          this.campaignsSearchForm = cachedData.campaignsSearchForm;
        }

        throw e;
      } finally {
        this.isInitialising = false;
      }
    },

    async doGetCompanyDetails(id: string, forceRefresh = false) {
      const cacheKey = 'companyDetails';

      // Return cached data if valid
      if (!forceRefresh && this.isCacheValid(cacheKey, id)) {
        this.company = this.cache[cacheKey].data;
        return this.company;
      }

      const response = await get(`/companies/${id}`);
      this.company = response;

      // Cache the result
      this.cache[cacheKey] = {
        data: response,
        timestamp: Date.now(),
        companyId: id,
      };

      return this.company;
    },
    async doGetCompany(id: string, forceRefresh = false) {
      const response = await get(`/companies/${id}`);
      return response;
    },

    async doGetCampaigns(company_id: string, forceRefresh = false) {
      const cacheKey = 'campaigns';

      // Return cached data if valid
      if (!forceRefresh && this.isCacheValid(cacheKey, company_id)) {
        this.campaigns = this.cache[cacheKey].data;
        return this.campaigns;
      }

      this.campaignsLoading = true;
      this.error = null;

      try {
        const data = await get(`/campaigns/all/companies/${company_id}`);

        const { ongoing_campaigns, upcoming_campaigns, finished_campaigns } = data;
        const finished = finished_campaigns.map((c: any) => ({ ...c, status: 'Completed' }));
        const ongoing = ongoing_campaigns.map((c: any) => ({ ...c, status: 'Ongoing' }));
        const upcoming = upcoming_campaigns.map((c: any) => ({ ...c, status: 'Upcoming' }));
        const campaignArr = [...ongoing, ...upcoming, ...finished];

        this.campaigns = campaignArr;

        // Cache the result
        this.cache[cacheKey] = {
          data: campaignArr,
          timestamp: Date.now(),
          companyId: company_id,
        };
      } catch (e) {
        this.error = e;
      }
      this.campaignsLoading = false;
    },

    async doSearchForCampaigns(company_id: string) {
      this.error = null;

      try {
        this.campaignsLoading = true;
        const data = await post(`/campaigns/search/companies/${company_id}`, this.campaignsSearchForm);
        this.campaignsFromSearch = data.search_results || [];
        this.totalCampaignsFromSearch = data.results_count;
      } catch (e) {
        this.error = e;
        throw e;
      }
      this.campaignsLoading = false;
    },

    async doGetContracts(company_id: string) {
      this.error = null;
      try {
        //const data = await get(`/companies/${company_id}/contracts`);
        const data = await get(`/contracts`);
        this.contracts = data.filter((e: any) => e.company_id === company_id);
      } catch (e) {
        this.error = e;
      }
    },

    async doCreateContractTemplate(form: any) {
      this.error = null;
      this.success = null;
      const data: { id: any; company_id: any; title: any; type: string; file?: any; body?: any } = {
        id: form.id,
        company_id: form.company_id,
        title: form.title,
        type: JSON.stringify(form.type),
      };
      if (form.file.length > 0) {
        data.file = form.file[0];
      } else {
        data.body = form.body;
      }
      try {
        const result = await post('/contract_templates', data, { 'Content-Type': 'multipart/form-data' });
        // Clear cache since data has changed
        this.clearCompanyCache(form.company_id);
        return result;
      } catch (e) {
        this.error = e;
        throw e;
      }
    },

    async doUpdateContractTemplate(form: any) {
      this.error = null;
      this.success = null;
      try {
        const { id } = form;
        const data: { company_id: any; title: any; type: string; file?: any; body?: any } = {
          company_id: form.company_id,
          title: form.title,
          type: JSON.stringify(form.type),
        };
        if (data.type === '"FILE"') {
          data.file = form.file[0];
        } else {
          data.body = form.body;
        }
        const result = await put(`/contract_templates/${id}`, data, { 'Content-Type': 'multipart/form-data' });
        // Clear cache since data has changed
        this.clearCompanyCache(form.company_id);
        return result;
      } catch (e) {
        this.error = e;
        throw e;
      }
    },

    async doDeleteContractTemplate(form: any) {
      this.error = null;
      this.success = null;
      try {
        await del(`/contract_templates/${form.id}`);
        // Clear cache since data has changed
        this.clearCompanyCache(form.company_id);
      } catch (e) {
        this.error = e;
        throw e;
      }
    },

    async doGetCompanyConfigs(id: string) {
      return (await get(`/companies/${id}/custom_config`)) as ICompanyConfigs;
    },

    // Cache management methods
    clearCompanyCache(companyId?: string) {
      if (companyId) {
        // Clear cache for specific company
        (Object.keys(this.cache) as Array<keyof typeof this.cache>).forEach(key => {
          if (this.cache[key]?.companyId === companyId) {
            this.cache[key] = { data: null, timestamp: null, companyId: null };
          }
        });
      } else {
        // Clear all cache
        (Object.keys(this.cache) as Array<keyof typeof this.cache>).forEach(key => {
          this.cache[key] = { data: null, timestamp: null, companyId: null };
        });
      }
    },
  },
});
